import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React from 'react';
import ScreeningProviderControls from './ScreeningProviderControls';
import type { SortableActiveProvidersProps } from './types';
import { getReorderedUpdates } from './reorderUtils';

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ listeners, attributes })}
    </div>
  );
}

const SortableActiveProviders = ({
  entries,
  editMode,
  onChange,
  activeProviderCount,
}: SortableActiveProvidersProps) => {
  const [ids, setIds] = React.useState(entries.map((e) => e.providerId));
  const sensors = useSensors(useSensor(PointerSensor));

  React.useEffect(() => {
    setIds(entries.map((e) => e.providerId));
  }, [entries]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    const updates = getReorderedUpdates(ids, active.id, over?.id, entries);

    if (!updates?.length) return;

    setIds(updates.map(({ providerId }) => providerId));

    updates.forEach(({ name, value }) => {
      onChange({
        target: { name, value },
      });
    });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {ids.map((id) => {
          const entry = entries.find((e) => e.providerId === id);
          if (!entry) return null;
          return (
            <SortableItem key={id} id={id}>
              {({ listeners, attributes }) => (
                <ScreeningProviderControls
                  data={entry}
                  editMode={editMode}
                  onChange={onChange}
                  activeProviderCount={activeProviderCount}
                  dragHandleProps={{ listeners, attributes }}
                />
              )}
            </SortableItem>
          );
        })}
      </SortableContext>
    </DndContext>
  );
};

export default SortableActiveProviders;
